import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import webservice from '../../../services/webservice';
import { AesEncrypt, AesDecrypt } from '../../../services/crypto';

const AddRemoteConfigModal = ({ show, onHide, onSuccess }) => {
  const [formData, setFormData] = useState({
    platform: 'ios',
    environment: 'staging',
    version: "1.0",
    isActive: true,
    config: {}
  });

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureType, setNewFeatureType] = useState('boolean');

  useEffect(() => {
    if (show) {
      fetchLatestConfig(formData.platform, formData.environment);
    }
  }, [formData.platform, formData.environment, show]);

  const fetchLatestConfig = async (platform, environment) => {
    setIsFetching(true);
    try {
      const response = await axios.post(`${webservice}api/getRemoteConfig`, {});
      if (response.data.response === 'S') {
        const decryptedData = AesDecrypt(response.data.data);
        if (decryptedData) {
          const allConfigs = JSON.parse(decryptedData);
          const platformConfigs = allConfigs.filter(c => c.platform === platform && c.environment === environment);
          const latestConfig = platformConfigs[0]; // Gets the most recent one since backend sorts by created_at -1
          
          if (latestConfig && latestConfig.config) {
            setFormData(prev => ({
              ...prev,
              config: latestConfig.config
            }));
          } else {
            // Reset to empty if no config exists for this platform/environment combo in DB
            setFormData(prev => ({
              ...prev,
              config: {}
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching latest config:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleAddFeature = () => {
    if (newFeatureName.trim()) {
      const key = newFeatureName.trim().toLowerCase().replace(/\s+/g, '_');
      setFormData(prev => ({
        ...prev,
        config: { ...prev.config, [key]: newFeatureType === 'boolean' ? false : '' }
      }));
      setNewFeatureName('');
    }
  };

  const handleDeleteFeature = (keyToDelete) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      delete newConfig[keyToDelete];
      return { ...prev, config: newConfig };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'version') {
      const regex = /^[0-9.]*$/;
      if (regex.test(value)) {
        setFormData(prev => ({ ...prev, version: value }));
      }
    } else if (name === 'platform' || name === 'environment') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'isActive') {
      setFormData(prev => ({ ...prev, isActive: e.target.checked }));
    } else {
      setFormData(prev => ({
        ...prev,
        config: { ...prev.config, [name]: value }
      }));
    }
  };

  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [name]: checked }
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Map to the desired backend schema
      const payload = {
        ...formData,
        updatedBy: localStorage.getItem('id') || "admin",
      };

      const encryptedData = AesEncrypt(JSON.stringify(payload));

      const response = await axios.post(`${webservice}api/addRemoteConfig`, { data: encryptedData });

      if (response.data.response === 'S') {
        onSuccess();
        onHide();
      } else {
        alert('Failed to add configuration');
      }
    } catch (error) {
      console.error('Error adding config:', error);
      alert('Error adding configuration');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', overflow: 'hidden', border: 'none', width: '100%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        {/* Header */}
        <div style={{ padding: '24px 30px', borderBottom: '1px solid #F1F5F9', background: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h4 style={{ margin: 0, fontWeight: '700', color: '#0F172A', fontSize: '20px' }}>Add New Configuration</h4>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748B', marginTop: '4px' }}>Create settings for iOS or Android applications.</p>
          </div>
          <button onClick={onHide} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#94A3B8', cursor: 'pointer' }}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '30px', overflowY: 'auto', flex: 1 }}>

          <div className="row mb-4">
            <div className="col-md-6 mb-3">
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Platform</label>
              <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: '10px', padding: '4px' }}>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, platform: 'ios' }))}
                  style={{ flex: 1, padding: '8px 0', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', background: formData.platform === 'ios' ? '#FFFFFF' : 'transparent', color: formData.platform === 'ios' ? '#0F172A' : '#64748B', boxShadow: formData.platform === 'ios' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  iOS
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, platform: 'android' }))}
                  style={{ flex: 1, padding: '8px 0', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', background: formData.platform === 'android' ? '#FFFFFF' : 'transparent', color: formData.platform === 'android' ? '#0F172A' : '#64748B', boxShadow: formData.platform === 'android' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  Android
                </button>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Environment</label>
              <select name="environment" value={formData.environment} onChange={handleInputChange} style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #E2E8F0', background: '#F8FAFC', fontSize: '14px', color: '#334155', outline: 'none' }}>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-12">
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Config Version</label>
              <input type="text" name="version" value={formData.version} onChange={handleInputChange} style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #E2E8F0', background: '#F8FAFC', fontSize: '14px', color: '#334155', outline: 'none' }} placeholder="e.g. 1.1.1" />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #F1F5F9' }}>
            <h5 style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Feature Toggles & Settings</h5>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #CBD5E1', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <input
                  type="text"
                  placeholder="New feature name..."
                  value={newFeatureName}
                  onChange={(e) => {
                    let val = e.target.value.toLowerCase();
                    val = val.replace(/\s+/g, '_');
                    val = val.replace(/[^a-z0-9_]/g, '');
                    setNewFeatureName(val);
                  }}
                  style={{ padding: '8px 14px', border: 'none', background: 'transparent', fontSize: '13px', outline: 'none', width: '220px', color: '#0F172A' }}
                />
                <div style={{ width: '1px', height: '24px', background: '#CBD5E1' }}></div>
                <select
                  value={newFeatureType}
                  onChange={(e) => setNewFeatureType(e.target.value)}
                  style={{ padding: '8px 14px', border: 'none', background: '#F8FAFC', fontSize: '13px', outline: 'none', color: '#475569', cursor: 'pointer', fontWeight: '500' }}
                >
                  <option value="boolean">Toggle</option>
                  <option value="string">Text</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleAddFeature}
                style={{ padding: '8px 16px', background: '#4F46E5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(79, 70, 229, 0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(79, 70, 229, 0.2)'; }}
              >
                <i className="fa fa-plus" style={{ fontSize: '11px' }}></i> Add
              </button>
            </div>
          </div>

          {Object.keys(formData.config).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#F8FAFC', borderRadius: '12px', border: '1px dashed #CBD5E1', color: '#64748B' }}>
              <i className="fa fa-sliders" style={{ fontSize: '32px', color: '#94A3B8', marginBottom: '16px' }}></i>
              <h6 style={{ fontSize: '15px', fontWeight: '600', color: '#334155', margin: '0 0 6px 0' }}>No Configuration Found</h6>
              <p style={{ fontSize: '13px', margin: 0 }}>There are no settings for this platform and environment. Add a new feature to get started.</p>
            </div>
          ) : (
            <>
              <div className="row mb-3">
                {Object.entries(formData.config).filter(([_, value]) => typeof value === 'boolean').map(([key, value]) => (
                  <div className="col-md-6" key={key}>
                    <div style={{ padding: '15px', background: '#F8FAFC', borderRadius: '12px', marginBottom: '15px', border: '1px solid #E2E8F0' }}>
                      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </span>
                        <div style={{ display: "flex", gap: "12px" , alignItems:"center", justifyContent:"center"}}>
                          <div style={{ position: 'relative', width: '42px', height: '24px' }}>
                            <input
                              type="checkbox"
                              name={key}
                              checked={value}
                              onChange={handleToggleChange}
                              style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                            />
                            <div style={{
                              width: '42px',
                              height: '24px',
                              borderRadius: '12px',
                              background: value ? '#10B981' : '#CBD5E1',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              transition: 'background 0.25s ease',
                            }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: '#FFFFFF',
                                position: 'absolute',
                                top: '2px',
                                left: value ? '20px' : '2px',
                                transition: 'transform 0.25s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                transform: value ? 'translateX(0)' : 'translateX(0)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }} />
                            </div>
                          </div>
                          <i className="fa fa-times" style={{ cursor: 'pointer', fontSize: '18px', color: "#ff0505ff", padding: '4px' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteFeature(key); }}></i>
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row">
                {Object.entries(formData.config).filter(([_, value]) => typeof value === 'string').map(([key, value]) => (
                  <div className="col-md-6 mb-3" key={key}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                      <i className="fa fa-times" style={{ cursor: 'pointer', color: "#ff0505ff", fontSize: '14px' }} onClick={() => handleDeleteFeature(key)}></i>
                    </label>
                    <input type="text" name={key} value={value} onChange={handleInputChange} style={{ width: '100%', padding: '10px 15px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none' }} placeholder={`Enter ${key.replace(/_/g, ' ')}...`} />
                  </div>
                ))}
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '20px 30px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button onClick={onHide} style={{ padding: '10px 20px', borderRadius: '10px', background: '#FFFFFF', border: '1px solid #CBD5E1', color: '#475569', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} style={{ padding: '10px 24px', borderRadius: '10px', background: '#4F46E5', border: 'none', color: 'white', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 10px rgba(79, 70, 229, 0.2)' }}>
            {loading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRemoteConfigModal;
